import * as React from "react";
import * as ReactDOM from "react-dom";
import { TypesOfCareFilters } from "./TypesOfCareFilters";
import { SearchFilters } from "./SearchFilters";
import { VacancyFilter } from "./VacancyFilter";
import { AgeFilter } from "./AgeFilter";
import { NQSRatingFilter } from "./NQSRatingFilter";
import { PriceFilter } from "./PriceFilter";
import { SpecialOffersFilter } from "./SpecialOffersFilter";
import { RatingFilter } from "./RatingFilter";

type filterObject = {
    typesOfCareFilters: typesOfCareFilters;
    searchFilters: searchFilters;
};

type typesOfCareFilters = {
    earlyEducationFilters: earlyEducationFilterObject;
    outOfHoursFilters: outOfHoursFilterObject;
};

type searchFilters = {
    onlyVacancies: boolean;
    daysRequired: daysRequiredFilterObject;
    overnightEvening: boolean;
    reviews: reviewsFilterObject;
    ratingStandards: ratingStandardFilterObject;
    childsAge: number;
    price: Array<number>;
    onlySpecials: boolean;
    location: locationFilterObject;
};

type earlyEducationFilterObject = {
    "child-care-centre": boolean;
    "family-day-care": boolean;
    "pre-school": boolean;
    all: boolean;
};

type outOfHoursFilterObject = {
    "before-school-care": boolean;
    "after-school-care": boolean;
    "occasional-care": boolean;
    "vacation-care": boolean;
    all: boolean;
};

type daysRequiredFilterObject = {
    mon: boolean;
    tue: boolean;
    wed: boolean;
    thu: boolean;
    fri: boolean;
    sat: boolean;
    sun: boolean;
};

type reviewsFilterObject = {
    fourStar: boolean;
    threeStar: boolean;
    twoStar: boolean;
    all: boolean;
};

type ratingStandardFilterObject = {
    exceeding: boolean;
    excellent: boolean;
    meeting: boolean;
    workingTowards: boolean;
};

type locationFilterObject = {
    exact: boolean;
    fiveKm: boolean;
    tenKm: boolean;
    fifteenKm: boolean;
};

type countObject = {
    typesOfCareFilters: number;
    searchFilters: number;
};

type eventDetailObject = {
    filterState: filterObject;
    filterCount: countObject;
};

type setFilterEventObject = {
    group: string;
    type: string;
    value: any;
};

type Props = {
    data: string;
};

// searchedFilters and searchedCount represent the filter state when last search was triggered.
// the normal filters and count keep track of the changes before applying the filters
type State = {
    filters: filterObject;
    count: countObject;
    showTypesOfCare: boolean;
    showSearchFilters: boolean;
    searchedFilters: filterObject;
    searchedCount: countObject;
    [key: string]: any;
};

const countState: countObject = {
    typesOfCareFilters: 0,
    searchFilters: 0,
};

declare var c4kCore: any;

// Min and Max Price Constant for Search Filters
const MINIMUM_ALLOWED_PRICE: number = 0;
const MAXIMUM_ALLOWED_PRICE: number = 350;

const filterState: filterObject = {
    typesOfCareFilters: {
        earlyEducationFilters: {
            "child-care-centre": false,
            "family-day-care": false,
            "pre-school": false,
            all: false,
        },
        outOfHoursFilters: {
            "before-school-care": false,
            "after-school-care": false,
            "occasional-care": false,
            "vacation-care": false,
            all: false,
        },
    },
    searchFilters: {
        onlyVacancies: false,
        daysRequired: {
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
            sun: false,
        },
        overnightEvening: false,
        reviews: {
            fourStar: false,
            threeStar: false,
            twoStar: false,
            all: false,
        },
        ratingStandards: {
            exceeding: false,
            excellent: false,
            meeting: false,
            workingTowards: false,
        },
        childsAge: 0,
        price: [MINIMUM_ALLOWED_PRICE, MAXIMUM_ALLOWED_PRICE],
        onlySpecials: false,
        location: {
            exact: false,
            fiveKm: false,
            tenKm: false,
            fifteenKm: false,
        },
    },
};

// Empty filters used for clearing/resetting filters
const emptyFilters: filterObject = {
    typesOfCareFilters: {
        earlyEducationFilters: {
            "child-care-centre": false,
            "family-day-care": false,
            "pre-school": false,
            all: false,
        },
        outOfHoursFilters: {
            "before-school-care": false,
            "after-school-care": false,
            "occasional-care": false,
            "vacation-care": false,
            all: false,
        },
    },
    searchFilters: {
        onlyVacancies: false,
        daysRequired: {
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
            sun: false,
        },
        overnightEvening: false,
        reviews: {
            fourStar: false,
            threeStar: false,
            twoStar: false,
            all: false,
        },
        ratingStandards: {
            exceeding: false,
            excellent: false,
            meeting: false,
            workingTowards: false,
        },
        childsAge: 0,
        price: [MINIMUM_ALLOWED_PRICE, MAXIMUM_ALLOWED_PRICE],
        onlySpecials: false,
        location: {
            exact: false,
            fiveKm: false,
            tenKm: false,
            fifteenKm: false,
        },
    },
};

const activeFilterButton: string = "button cta-filter";
const inactiveFilterButton: string =
    "button cta-tertiary border border-skin-neutral-light text-base font-normal";
const inactiveFilterButtonV3: string =
    "w-full text-[#3A3A3A] text-xs whitespace-nowrap px-[17px] py-[11px] bg-[#EFEFFD] rounded-[999px] flex items-center gap-2";
const activeFilterButtonV3: string =
    "w-full text-white font-bold text-xs whitespace-nowrap px-[17px] py-[11px] bg-[#5A60EC] rounded-[999px] flex items-center gap-2";

class SearchFiltersComponent extends React.Component<Props, State> {
    locationModel: any = {};

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.locationModel = window.searchResult;
        window.addEventListener("opentypesofcarefilters", (event) => {
            this.showTypesOfCareModal();
        });

        window.addEventListener("opensearchfilters", (event) => {
            this.showSearchFiltersModal();
        });

        window.addEventListener("clearsearchfilters", (event) => {
            this.ClearFilters(true);
        });

        // This function is called when filters are populated via history state of the page
        window.addEventListener("updatefilterstate", (event: CustomEvent) => {
            this.forceFilterState(event.detail);
        });

        // This function is called when filters are updated outside of the filter modal and a search needs to be triggered
        window.addEventListener(
            "forcefilterstateandsearch",
            (event: CustomEvent) => {
                this.forceFilterStateAndSearch(event.detail);
            }
        );

        // Get the criteria from props and use it to manipulate the filters
        let criteria = JSON.parse(this.props.data);
        this.forceCriteriaFilters(criteria);
    }

    state: State = {
        filters: filterState,
        count: countState,
        showTypesOfCare: false,
        showSearchFilters: false,
        searchedFilters: filterState,
        searchedCount: countState,
    };

    // This function takes the criteria from props/server and forces the values into the filter state
    forceCriteriaFilters = (criteria) => {
        // Format the age to represent months
        let childAge = criteria.ChildAge;
        let childAgeUnit = criteria.ChildAgeUnit;
        switch (childAgeUnit) {
            case "years":
                childAge = childAge * 12;
                break;
            case "weeks":
                childAge = Math.floor(childAge / (52 / 12));
                break;
        }

        // Make a new filter object and populate values based on the criteria object
        let filters = {
            typesOfCareFilters: {
                earlyEducationFilters: {
                    "child-care-centre":
                        criteria.Types?.includes("child-care-centre") ||
                        criteria.ProviderType == "child-care-centre"
                            ? true
                            : false,
                    "family-day-care":
                        criteria.Types?.includes("family-day-care") ||
                        criteria.ProviderType == "family-day-care"
                            ? true
                            : false,
                    "pre-school":
                        criteria.Types?.includes("pre-school") ||
                        criteria.ProviderType == "pre-school"
                            ? true
                            : false,
                    all:
                        criteria.Types?.includes("child-care-centre") &&
                        criteria.Types.includes("family-day-care") &&
                        criteria.Types.includes("pre-school")
                            ? true
                            : false,
                },
                outOfHoursFilters: {
                    "before-school-care":
                        criteria.Types?.includes("before-school-care") ||
                        criteria.ProviderType == "before-school-care"
                            ? true
                            : false,
                    "after-school-care":
                        criteria.Types?.includes("after-school-care") ||
                        criteria.ProviderType == "after-school-care"
                            ? true
                            : false,
                    "occasional-care":
                        criteria.Types?.includes("occasional-care") ||
                        criteria.ProviderType == "occasional-care"
                            ? true
                            : false,
                    "vacation-care":
                        criteria.Types?.includes("vacation-care") ||
                        criteria.ProviderType == "vacation-care"
                            ? true
                            : false,
                    all:
                        criteria.Types?.includes("before-school-care") &&
                        criteria.Types?.includes("after-school-care") &&
                        criteria.Types.includes("occasional-care") &&
                        criteria.Types.includes("vacation-care")
                            ? true
                            : false,
                },
            },
            searchFilters: {
                onlyVacancies: criteria.Show == "vacancy-only" ? true : false,
                daysRequired: {
                    mon: criteria.Day?.includes("mon") ? true : false,
                    tue: criteria.Day?.includes("tue") ? true : false,
                    wed: criteria.Day?.includes("wed") ? true : false,
                    thu: criteria.Day?.includes("thu") ? true : false,
                    fri: criteria.Day?.includes("fri") ? true : false,
                    sat: criteria.Day?.includes("sat") ? true : false,
                    sun: criteria.Day?.includes("sun") ? true : false,
                },
                overnightEvening: criteria.OvernightCare ? true : false,
                reviews: {
                    fourStar: criteria.RatingsMin == 4 ? true : false,
                    threeStar: criteria.RatingsMin == 3 ? true : false,
                    twoStar: criteria.RatingsMin == 2 ? true : false,
                    all: false,
                },
                ratingStandards: {
                    exceeding: criteria.NqrMin == 1 ? true : false,
                    excellent: criteria.NqrMin == 2 ? true : false,
                    meeting: criteria.NqrMin == 3 ? true : false,
                    workingTowards: criteria.NqrMin == 4 ? true : false,
                },
                childsAge: childAge,
                price: [
                    criteria.PriceMin || MINIMUM_ALLOWED_PRICE,
                    criteria.PriceMax || MAXIMUM_ALLOWED_PRICE,
                ],
                onlySpecials: criteria.SpecialOffers == "yes" ? true : false,
                location: {
                    exact: false,
                    fiveKm: criteria.Location == 5 ? true : false,
                    tenKm: criteria.Location == 10 ? true : false,
                    fifteenKm: criteria.Location == 15 ? true : false,
                },
            },
        };

        // Count the number of types of care filters that are on
        let newTocCount: number = Object.entries(
            filters.typesOfCareFilters.earlyEducationFilters
        ).filter(([key, value]) => value == true && key != "all").length;
        newTocCount += Object.entries(
            filters.typesOfCareFilters.outOfHoursFilters
        ).filter(([key, value]) => value == true && key != "all").length;

        // Count the number of search filters that are on
        let newSFCount: number =
            Object.entries(filters.searchFilters.daysRequired).filter(
                ([key, value]) => value == true && key != "all"
            ).length > 0
                ? 1
                : 0;
        newSFCount += Object.entries(filters.searchFilters.reviews).filter(
            ([key, value]) => value == true && key != "all"
        ).length;
        newSFCount += Object.entries(
            filters.searchFilters.ratingStandards
        ).filter(([key, value]) => value == true && key != "all").length;
        newSFCount += Object.entries(filters.searchFilters.location).filter(
            ([key, value]) => value == true && key != "all"
        ).length;
        newSFCount += filters.searchFilters.onlyVacancies ? 1 : 0;
        newSFCount += filters.searchFilters.onlySpecials ? 1 : 0;
        newSFCount += filters.searchFilters.childsAge > 0 ? 1 : 0;
        newSFCount +=
            filters.searchFilters.price[0] > MINIMUM_ALLOWED_PRICE ? 1 : 0;
        newSFCount +=
            filters.searchFilters.price[1] < MAXIMUM_ALLOWED_PRICE ? 1 : 0;
        newSFCount += filters.searchFilters.overnightEvening ? 1 : 0;

        let count = {
            typesOfCareFilters: newTocCount,
            searchFilters: newSFCount,
        };

        if (this.getCookie("srpVersion") == "v3") {
            const countVacancy = Object.entries(
                filters.searchFilters.daysRequired
            ).filter(([key, value]) => value == true && key != "all").length;
            const countAge = filters.searchFilters.childsAge > 0 ? 1 : 0;
            let countPrice: number = 0;
            countPrice +=
                filters.searchFilters.price[0] > MINIMUM_ALLOWED_PRICE ? 1 : 0;
            countPrice +=
                filters.searchFilters.price[1] < MAXIMUM_ALLOWED_PRICE ? 1 : 0;
            const countNQS = Object.entries(
                filters.searchFilters.ratingStandards
            ).filter(([key, value]) => value == true && key != "all").length;
            const countSpecial = filters.searchFilters.onlySpecials ? 1 : 0;
            const countRating = Object.entries(
                filters.searchFilters.reviews
            ).filter(([key, value]) => value == true && key != "all").length;

            this.setState({
                countVacancy,
                countAge,
                countPrice,
                countNQS,
                countSpecial,
                countRating,
            });
        }

        // Set state for filters and count
        this.setState({
            filters: filters,
            count: count,
            searchedFilters: JSON.parse(JSON.stringify(filters)),
            searchedCount: JSON.parse(JSON.stringify(count)),
        });

        // Send the new data to alpine to update its variables
        var evt = new CustomEvent("updatefiltersandcount", {
            detail: { filters: filters, count: count },
        });
        window.dispatchEvent(evt);
    };

    forceFilterState = (detail: eventDetailObject) => {
        if (detail.filterState == null) {
            this.setState({
                filters: emptyFilters,
                count: {
                    typesOfCareFilters: 0,
                    searchFilters: 0,
                },
            });
        } else {
            this.setState({
                filters: detail.filterState,
                count: {
                    typesOfCareFilters: detail.filterCount.typesOfCareFilters,
                    searchFilters: detail.filterCount.searchFilters,
                },
            });
        }
    };

    // The event detail object must have filter group, type and value as per filter state objects structure
    forceFilterStateAndSearch = (detail: setFilterEventObject) => {
        let clonedFilters = this.state.searchedFilters;

        // This line will break if you don't provide the group and type as well as value
        clonedFilters[detail.group][detail.type] = detail.value;

        // Count the number of types of care filters that are on
        let newTocCount: number = Object.entries(
            clonedFilters.typesOfCareFilters.earlyEducationFilters
        ).filter(([key, value]) => value == true && key != "all").length;
        newTocCount += Object.entries(
            clonedFilters.typesOfCareFilters.outOfHoursFilters
        ).filter(([key, value]) => value == true && key != "all").length;

        // Count the number of search filters that are on
        let newSFCount: number =
            Object.entries(clonedFilters.searchFilters.daysRequired).filter(
                ([key, value]) => value == true && key != "all"
            ).length > 0
                ? 1
                : 0;
        newSFCount += Object.entries(
            clonedFilters.searchFilters.reviews
        ).filter(([key, value]) => value == true && key != "all").length;
        newSFCount += Object.entries(
            clonedFilters.searchFilters.ratingStandards
        ).filter(([key, value]) => value == true && key != "all").length;
        newSFCount += Object.entries(
            clonedFilters.searchFilters.location
        ).filter(([key, value]) => value == true && key != "all").length;
        newSFCount += clonedFilters.searchFilters.onlyVacancies ? 1 : 0;
        newSFCount += clonedFilters.searchFilters.onlySpecials ? 1 : 0;
        newSFCount += clonedFilters.searchFilters.childsAge > 0 ? 1 : 0;
        newSFCount +=
            clonedFilters.searchFilters.price[0] > MINIMUM_ALLOWED_PRICE
                ? 1
                : 0;
        newSFCount +=
            clonedFilters.searchFilters.price[1] < MAXIMUM_ALLOWED_PRICE
                ? 1
                : 0;

        let count = {
            typesOfCareFilters: newTocCount,
            searchFilters: newSFCount,
        };
        if (this.getCookie("srpVersion") == "v3") {
            const countNQS = Object.entries(
                clonedFilters.searchFilters.ratingStandards
            ).filter(([key, value]) => value == true && key != "all").length;

            this.setState({
                countNQS,
            });
        }
        // Set state for filters and count
        this.setState(
            {
                filters: clonedFilters,
                count: count,
                searchedFilters: JSON.parse(JSON.stringify(clonedFilters)),
                searchedCount: JSON.parse(JSON.stringify(count)),
            },
            () => this.doSearch()
        );
    };

    showTypesOfCareModal = () => {
        // When modal opens, always populate it with the last searched state of filters
        let cloneSearchedFilters = JSON.parse(
            JSON.stringify(this.state.searchedFilters)
        );
        let cloneSearchedCount = JSON.parse(
            JSON.stringify(this.state.searchedCount)
        );
        this.getStateUsingPostcode(parseInt(this.locationModel.postcode));
        let eventSuburb = `${this.locationModel.suburbName}, ${this.locationModel.stateAbbreviation}, ${this.locationModel.postcode}`;
        let eventLabel = `type=filter_type|source=Srp`;
        this.setState({
            filters: cloneSearchedFilters,
            count: cloneSearchedCount,
            showTypesOfCare: true,
        });
        c4kCore.analyticsTrackEvent(
            "Search Results",
            "Types of Care Filter",
            eventLabel,
            eventSuburb
        );
        document.getElementById("body").style.position = "fixed";
        document.getElementById("body").style.width = "100%";
    };

    showSearchFiltersModal = () => {
        // When modal opens, always populate it with the last searched state of filters
        let cloneSearchedFilters = JSON.parse(
            JSON.stringify(this.state.searchedFilters)
        );
        let cloneSearchedCount = JSON.parse(
            JSON.stringify(this.state.searchedCount)
        );
        this.getStateUsingPostcode(parseInt(this.locationModel.postcode));
        let eventSuburb = `${this.locationModel.suburbName}, ${this.locationModel.stateAbbreviation}, ${this.locationModel.postcode}`;
        let eventLabel = `type=filter_all|source=Srp`;

        this.setState({
            filters: cloneSearchedFilters,
            count: cloneSearchedCount,
            showSearchFilters: true,
        });
        c4kCore.analyticsTrackEvent(
            "Search Results",
            "Search Filters",
            eventLabel,
            eventSuburb
        );
        document.getElementById("body").style.position = "fixed";
        document.getElementById("body").style.width = "100%";
    };

    showFilterModal = (filterType: string) => {
        // Khi modal mở, luôn điền vào trạng thái bộ lọc đã tìm kiếm lần cuối
        let cloneSearchedFilters = JSON.parse(
            JSON.stringify(this.state.searchedFilters)
        );
        let cloneSearchedCount = JSON.parse(
            JSON.stringify(this.state.searchedCount)
        );

        this.getStateUsingPostcode(parseInt(this.locationModel.postcode));

        let eventSuburb = `${this.locationModel.suburbName}, ${this.locationModel.stateAbbreviation}, ${this.locationModel.postcode}`;
        let eventLabel = `type=filter_all|source=Srp`;

        this.setState((prevState) => ({
            ...prevState,
            filters: cloneSearchedFilters,
            count: cloneSearchedCount,
            [filterType]: true,
        }));

        c4kCore.analyticsTrackEvent(
            "Search Results",
            "Search Filters",
            eventLabel,
            eventSuburb
        );

        document.getElementById("body").style.position = "fixed";
        document.getElementById("body").style.width = "100%";
    };

    showVacancyFiltersModal = () => {
        this.showFilterModal("showVacancyFilters");
    };

    showRatingFiltersModal = () => {
        this.showFilterModal("showRatingFilters");
    };

    showAgeFiltersModal = () => {
        this.showFilterModal("showAgeFilters");
    };

    showNQSRatingFiltersModal = () => {
        this.showFilterModal("showNQSRatingFilters");
    };

    showPriceFiltersModal = () => {
        this.showFilterModal("showPriceFilters");
    };

    showSpecialOffersFiltersModal = () => {
        this.showFilterModal("showSpecialOffersFilters");
    };

    closeTypesOfCareFiltersModal = () => {
        this.setState({
            showTypesOfCare: false,
        });
        document.getElementById("body").style.position = "static";
    };

    closeSearchFiltersModal = () => {
        this.setState((prev) => ({
            ...prev,
            showSearchFilters: false,
            showVacancyFilters: false,
            showRatingFilters: false,
            showAgeFilters: false,
            showNQSRatingFilters: false,
            showPriceFilters: false,
            showSpecialOffersFilters: false,
        }));
        document.getElementById("body").style.position = "static";
    };

    updateTypesOfCareFilters = (filters) => {
        let cloneFilters: filterObject = this.state.filters;
        cloneFilters.typesOfCareFilters = filters;

        this.setState({
            filters: cloneFilters,
        });
    };

    updateTypesOfCareCount = (count) => {
        let cloneCount: countObject = this.state.count;
        cloneCount.typesOfCareFilters = count;

        this.setState({
            count: cloneCount,
        });
    };

    updateSearchFilters = (filters) => {
        let cloneFilters: filterObject = this.state.filters;
        cloneFilters.searchFilters = filters;

        this.setState({
            filters: cloneFilters,
        });
    };

    updateCountVacancies = (count) => {
        this.setState({ countVacancy: count });
    };

    updateCountRating = (count) => {
        this.setState({ countRating: count });
    };

    updateCountAge = (count) => {
        this.setState({ countAge: count });
    };

    updateCountNQS = (count) => {
        this.setState({ countNQS: count });
    };
    updateCountPrice = (count) => {
        this.setState({ countPrice: count });
    };
    updateCountSpecial = (count) => {
        this.setState({ countSpecial: count });
    };

    updateSearchCount = (count) => {
        let cloneCount: countObject = this.state.count;
        cloneCount.searchFilters = count;

        this.setState({
            count: cloneCount,
        });
    };

    updateCount = {
        vacancy: (count) => this.setState({ countVacancy: count }),
        rating: (count) => this.setState({ countRating: count }),
        age: (count) => this.setState({ countAge: count }),
        nqs: (count) => this.setState({ countNQS: count }),
        price: (count) => this.setState({ countPrice: count }),
        special: (count) => this.setState({ countSpecial: count }),
    };


    doSearch = () => {
        var evt1 = new CustomEvent("searchfilterscount", {
            detail: this.state.count.searchFilters,
        });
        window.dispatchEvent(evt1);

        var evt2 = new CustomEvent("typesofcarecount", {
            detail: this.state.count.typesOfCareFilters,
        });
        window.dispatchEvent(evt2);

        var evt3 = new CustomEvent("searchfilterschanged", {
            detail: this.state.filters,
        });
        window.dispatchEvent(evt3);

        let cloneFilters = JSON.parse(JSON.stringify(this.state.filters));
        let cloneCount = JSON.parse(JSON.stringify(this.state.count));

        this.setState({
            searchedFilters: cloneFilters,
            searchedCount: cloneCount,
        });
    };


    // Clear Filters
    ClearFilters = (search: boolean = false) => {
        // Update state to reflect the cleared filters
        this.setState(
            {
                filters: emptyFilters,
                count: {
                    typesOfCareFilters: 0,
                    searchFilters: 0,
                },
                countVacancy: 0,
                countPrice: 0,
                countAge: 0,
                countNQS: 0,
                countSpecial: 0,
                countRating: 0,
            },
            () => {
                if (search) {
                    this.doSearch();
                }
            }
        );
    };
    getStateUsingPostcode = (postcode: number) => {
        switch (true) {
            case postcode >= 2000 && postcode <= 2599:
            case postcode >= 2619 && postcode <= 2899:
            case postcode >= 2921 && postcode <= 2999:
                this.locationModel.stateAbbreviation = "NSW";
                break;
            case postcode >= 2600 && postcode <= 2618:
            case postcode >= 2900 && postcode <= 2920:
                this.locationModel.stateAbbreviation = "ACT";
                break;
            case postcode >= 3000 && postcode <= 3999:
                this.locationModel.stateAbbreviation = "VIC";
                break;
            case postcode >= 4000 && postcode <= 4999:
                this.locationModel.stateAbbreviation = "QLD";
                break;
            case postcode >= 5000 && postcode <= 5799:
                this.locationModel.stateAbbreviation = "SA";
                break;
            case postcode >= 6000 && postcode <= 6797:
                this.locationModel.stateAbbreviation = "WA";
                break;
            case postcode >= 7000 && postcode <= 7799:
                this.locationModel.stateAbbreviation = "TAS";
                break;
            case postcode >= 800 && postcode <= 899:
                this.locationModel.stateAbbreviation = "NT";
                break;
        }
    };

    getCookie = (name) => {
        const parts = document.cookie
            .split(";")
            .map((c) => c.trim().split("="));
        for (let [k, v] of parts) {
            if (k === name) return v;
        }
        return null;
    };

    checkIsSrpVersionV3 = () => {
        if (this.getCookie("srpVersion") == "v3") {
            return true;
        }
        return false;
    };

    render() {
        return (
            <React.Fragment>
                <div
                    x-data="{}"
                    className="flex flex-row overflow-auto gap-1.5 srp-filters-container"
                >
                    {(window.innerWidth > 768 ||
                        this.getCookie("srpVersion") !== "v3") && (
                        <button
                            id='srp-type-of-care-button'
                            className={
                                this.state.searchedCount.typesOfCareFilters > 0
                                    ? activeFilterButton
                                    : inactiveFilterButton
                            }
                            onClick={() => this.showTypesOfCareModal()}
                        >
                            <span className="flex flex-row items-center gap-1">
                                <span>Types of Care</span>
                                {this.state.searchedCount.typesOfCareFilters > 0 && (
                                    <React.Fragment>
                                        <span className="dot"></span>
                                        <span>
                                            {
                                                this.state.searchedCount
                                                    .typesOfCareFilters
                                            }
                                        </span>
                                    </React.Fragment>
                                )}
                            </span>
                        </button>
                    )}

                    {this.getCookie("srpVersion") == "v3" &&
                        window.innerWidth <= 768 && (
                            <React.Fragment>
                                {(this.state.count.searchFilters > 0 ||
                                    this.state.count.typesOfCareFilters > 0 ||
                                    this.state.countVacancy > 0 ||
                                    this.state.countPrice > 0 ||
                                    this.state.countAge > 0 ||
                                    this.state.countNQS > 0 ||
                                    this.state.countSpecial > 0 ||
                                    this.state.countRating > 0) && (
                                    <button
                                        className="bg-[#FEE5FE] w-full text-[#3A3A3A] text-xs whitespace-nowrap px-[17px] py-[11px] rounded-[999px]"
                                        onClick={() => this.ClearFilters(true)}
                                    >
                                        Reset Filters
                                    </button>
                                )}

                                <button
                                    className={`${
                                        this.state.searchedCount
                                            .typesOfCareFilters > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() => this.showTypesOfCareModal()}
                                >
                                    <span className="flex flex-row items-center gap-1">
                                        <span>
                                            All Care Types{" "}
                                        {this.state.searchedCount
                                            .typesOfCareFilters > 0 &&
                                            `(${this.state.searchedCount
                                                .typesOfCareFilters
                                            })`}
                                        </span>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="7"
                                            viewBox="0 0 12 7"
                                            fill="none"
                                        >
                                            <path
                                                d="M1 1L6 6L11 1"
                                                stroke="currentColor"
                                                stroke-width="1.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </span>
                                </button>

                                <button
                                    className={`${
                                        this.state.countVacancy > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() =>
                                        this.showVacancyFiltersModal()
                                    }
                                >
                                    Vacancy{" "}
                                    {this.state.countVacancy > 0 &&
                                        `(${this.state.countVacancy})`}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="7"
                                        viewBox="0 0 12 7"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                                <button
                                    className={`${
                                        this.state.countRating > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() =>
                                        this.showRatingFiltersModal()
                                    }
                                >
                                    Rating{" "}
                                    {this.state.countRating > 0 &&
                                        `(${this.state.countRating})`}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="7"
                                        viewBox="0 0 12 7"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                                <button
                                    className={`${
                                        this.state.countAge > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() => this.showAgeFiltersModal()}
                                >
                                    Age{" "}
                                    {this.state.countAge > 0 &&
                                        `(${this.state.countAge})`}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="7"
                                        viewBox="0 0 12 7"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                                <button
                                    className={`${
                                        this.state.countNQS > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() =>
                                        this.showNQSRatingFiltersModal()
                                    }
                                >
                                    NQS Rating{" "}
                                    {this.state.countNQS > 0 &&
                                        `(${this.state.countNQS})`}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="7"
                                        viewBox="0 0 12 7"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                                <button
                                    className={`${
                                        this.state.countPrice > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() => this.showPriceFiltersModal()}
                                >
                                    Price{" "}
                                    {this.state.countPrice > 0 &&
                                        `(${this.state.countPrice})`}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="7"
                                        viewBox="0 0 12 7"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                                <button
                                    className={`${
                                        this.state.countSpecial > 0
                                            ? activeFilterButtonV3
                                            : inactiveFilterButtonV3
                                    } srp-v3-filter`}
                                    onClick={() =>
                                        this.showSpecialOffersFiltersModal()
                                    }
                                >
                                    Special Offers{" "}
                                    {this.state.countSpecial > 0 &&
                                        `(${this.state.countSpecial})`}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="7"
                                        viewBox="0 0 12 7"
                                        fill="none"
                                    >
                                        <path
                                            d="M1 1L6 6L11 1"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </svg>
                                </button>
                            </React.Fragment>
                        )}

                    {(window.innerWidth > 768 ||
                        this.getCookie("srpVersion") !== "v3") && (
                        <button
                            id='all-filter-button'
                            className={
                                this.state.searchedCount.searchFilters > 0
                                    ? activeFilterButton
                                    : inactiveFilterButton
                            }
                            onClick={() => this.showSearchFiltersModal()}
                        >
                            <span className="flex flex-row items-center gap-1">
                                <span>All Filters</span>
                                {this.state.searchedCount.searchFilters > 0 && (
                                    <React.Fragment>
                                        <span className="dot"></span>
                                        <span>
                                            {
                                                this.state.searchedCount
                                                    .searchFilters
                                            }
                                        </span>
                                    </React.Fragment>
                                )}
                            </span>
                        </button>
                        )}

                        <button x-on:click="$store.search.toggleMap();" className="hidden lg:inline bg-black/80 rounded-full px-6 py-3">
						<span className="flex items-center justify-center gap-2">
							<svg className="w-4 h-4"><use xlinkHref="/img/icons/srp-profile-core-sprite.svg#map-with-pin-white"></use></svg>
							<span className="text-base-15 font-bold text-white">
								Fullscreen map
							</span>
						</span>
                    </button>

                    {c4kCore.getCookie('srpVersion') !== 'v3' || window.innerWidth > 768 && 
						<span className="px-2 items-center gap-1.5" x-bind:class="$store.search.loading == true ? 'flex' : 'hidden'">
							<img src="/img/navy-spinner.svg" alt="Spinner blue dots circle" height="24" width="24"/><span>searching...</span>
						</span>
					}

					<TypesOfCareFilters
						closeModal={this.closeTypesOfCareFiltersModal}
						show={this.state.showTypesOfCare}
						updateFilters={this.updateTypesOfCareFilters}
						updateCount={this.updateTypesOfCareCount}
						filters={this.state.filters.typesOfCareFilters}
						count={this.state.count.typesOfCareFilters}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel = {this.locationModel}
					/>
					<SearchFilters
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showSearchFilters}
						updateFilters={this.updateSearchFilters}
                        updateCount={this.updateSearchCount}
                        updateCountOTP={this.updateCount}
						filters={this.state.filters.searchFilters}
						count={this.state.count.searchFilters}
						minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
						maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel= {this.locationModel}
					/>
					<VacancyFilter
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showVacancyFilters}
						updateFilters={this.updateSearchFilters}
                        updateCountVacancies={this.updateCountVacancies}
                        updateCount={this.updateSearchCount}
						filters={this.state.filters.searchFilters}
                        count={this.state.countVacancy}
                        minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
                        maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel={this.locationModel}
					/>
					<RatingFilter
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showRatingFilters}
						updateFilters={this.updateSearchFilters}
                        updateCountRating={this.updateCountRating}
                        updateCount={this.updateSearchCount}
						filters={this.state.filters.searchFilters}
                        count={this.state.countRating}
                        minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
                        maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel={this.locationModel}
					/>
					<AgeFilter
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showAgeFilters}
						updateFilters={this.updateSearchFilters}
                        updateCountAge={this.updateCountAge}
                        updateCount={this.updateSearchCount}
						filters={this.state.filters.searchFilters}
                        count={this.state.countAge}
                        minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
                        maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel={this.locationModel}
					/>
					<NQSRatingFilter
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showNQSRatingFilters}
						updateFilters={this.updateSearchFilters}
                        updateCountNQS={this.updateCountNQS}
                        updateCount={this.updateSearchCount}
						filters={this.state.filters.searchFilters}
                        count={this.state.countNQS}
                        minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
                        maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel={this.locationModel}
					/>
					<PriceFilter
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showPriceFilters}
						updateFilters={this.updateSearchFilters}
                        updateCountPrice={this.updateCountPrice}
                        updateCount={this.updateSearchCount}
						filters={this.state.filters.searchFilters}
						count={this.state.countPrice}
						minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
						maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel={this.locationModel}
					/>
					<SpecialOffersFilter
						closeModal={this.closeSearchFiltersModal}
						show={this.state.showSpecialOffersFilters}
						updateFilters={this.updateSearchFilters}
                        updateCountSpecial={this.updateCountSpecial}
                        updateCount={this.updateSearchCount}
						filters={this.state.filters.searchFilters}
						count={this.state.countSpecial}
						minimumAllowedPrice={MINIMUM_ALLOWED_PRICE}
						maximumAllowedPrice={MAXIMUM_ALLOWED_PRICE}
						doSearch={this.doSearch}
						clearFilters={this.ClearFilters}
						locationModel={this.locationModel}
					/>
				</div>
				{c4kCore.getCookie('srpVersion') == 'v3' && window.innerWidth <= 768 && (
					<span className="px-2 items-center gap-1.5 py-2" x-bind:class="$store.search.loading == true ? 'flex' : 'hidden'">
						<img src="/img/navy-spinner.svg" alt="Spinner blue dots circle" height="24" width="24"/><span>searching...</span>
					</span>
				)}
				
			</React.Fragment>
		);
	}
}

(window as any).RenderSearchFiltersComponent = function (props) {
    ReactDOM.render(
        <SearchFiltersComponent {...props} />,
        document.getElementById("searchFiltersComponent")
    );
};
